import { Box, Grid, Stack } from '@mui/material'
import { C1Button } from 'c1ui'
import { SelectOneAppHook } from 'components/core/search/apps'
import { QueryFilter } from 'components/core/search-filter/query'
import { AppValue } from 'lib/actions/apps'
import { SearchAppSecretsWithAlertCountRequest } from 'pbts/c1/api/app/v1/app_secret'
import { useCallback, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'

type IdentitiesFilterProps = {
    filter: SearchAppSecretsWithAlertCountRequest
    onFilterChange: (filter: SearchAppSecretsWithAlertCountRequest) => void
    hideAppFilter?: boolean
}

export const AppSecretsFilter = ({
    filter: _filter,
    onFilterChange: _onFilterChange,
    hideAppFilter = false,
}: IdentitiesFilterProps): React.ReactElement => {
    const initialFilter = useRef(_filter).current
    const [filter, setFilter] = useState<SearchAppSecretsWithAlertCountRequest>(initialFilter)
    const [showClear, setShowClear] = useState(false)
    const [selectedApp, setSelectedApp] = useState<AppValue | null>(null)

    const clear = useCallback((): void => {
        setSelectedApp(null)
        setFilter(initialFilter)
        _onFilterChange(initialFilter)
        setShowClear(false)
    }, [_onFilterChange, initialFilter])

    const updateFilter = useCallback((newFilter: SearchAppSecretsWithAlertCountRequest): void => {
        setFilter(newFilter)
        _onFilterChange(newFilter)
        if ((!hideAppFilter && newFilter.appId) || newFilter.query) {
            setShowClear(true)
        }
    }, [_onFilterChange, hideAppFilter])

    const onQueryChange = useCallback((query: string): void => {
        const newFilter = SearchAppSecretsWithAlertCountRequest.create({
            ...filter,
            query,
        })
        updateFilter(newFilter)
    }, [filter, updateFilter])

    const onAppSelected = useCallback((app: AppValue | null): void => {
        setSelectedApp(app || null)
        const newFilter = SearchAppSecretsWithAlertCountRequest.create({
            ...filter,
            appId: app?.id,
        })
        updateFilter(newFilter)
    }, [filter, updateFilter])

    return <Stack
        direction="row"
        gap={2}
        width="100%"
        alignItems="center"
    >
        <Box sx={{ width: '300px' }}>
            <QueryFilter
                autoFocus
                query={filter?.query || ''}
                setQuery={(q): void => onQueryChange(q as unknown as string)}
                onChange={onQueryChange}
            />
        </Box>

        {!hideAppFilter && <Box sx={{ width: '300px' }}>
            <SelectOneAppHook
                onSelected={onAppSelected}
                value={selectedApp}
                hideLabel
            />
        </Box>}

        {
            showClear && <Grid key="clear" item>
                <C1Button onClick={clear} variant="text" color="info">
                    <FormattedMessage id="components_clear_label" defaultMessage="Clear" />
                </C1Button>
            </Grid>
        }

    </Stack>
}
